import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { get } from "@/lib/api";
import { EmptyState, PageHeader, SkeletonRows } from "@/components/app/Common";
import { CandidatesTable } from "@/pages/candidates/CandidatesTable";
import { cn } from "@/lib/utils";
import { History, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { AddCandidateButton } from "@/components/intake/AddCandidateButton";

function IntakeActions() {
  const { user } = useAuth();
  if (!STAFF.includes(user?.role)) return null;
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="ghost" asChild data-tour="pt-import-history-link" data-testid="import-history-link"><Link to="/app/candidates/imports"><History size={16} aria-hidden="true" /> Import history</Link></Button>
      <Button variant="outline" asChild data-tour="pt-bulk-import-btn" data-testid="bulk-import-btn"><Link to="/app/candidates/import"><Upload size={16} aria-hidden="true" /> Bulk import</Link></Button>
      <AddCandidateButton testId="candidates-add-candidate-btn" />
    </div>
  );
}

function NeedsAttention() {
  const { data, isLoading } = useQuery({ queryKey: ["needs-attention"], queryFn: () => get("/candidates/needs-attention"), refetchInterval: 10000 });
  if (isLoading) return <SkeletonRows rows={3} />;
  if (!data.length) return <EmptyState testId="needs-attention-empty" icon={CheckCircle2} title="Nothing needs attention" text="Every resume was read successfully." />;
  return (
    <div className="panel divide-y divide-line" data-tour="pt-needs-attention-list" data-testid="needs-attention-list">
      {data.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={`needs-attention-${c.id}`}>
          <AlertTriangle size={18} className="text-caution" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-medium text-ink">{c.display_name}</p>
            <p className="text-xs text-ink-secondary" data-testid={`needs-attention-reason-${c.id}`}>{c.parse_error}</p>
          </div>
          <Link to={`/app/candidates/${c.id}/resume`} className="text-sm text-brand hover:underline" data-testid={`needs-attention-open-${c.id}`}>Enter details manually</Link>
        </div>
      ))}
    </div>
  );
}

export default function CandidatesList() {
  const [tab, setTab] = useState("all");
  const { data: attention } = useQuery({ queryKey: ["needs-attention"], queryFn: () => get("/candidates/needs-attention") });
  const tabs = [["all", "All candidates"], ["attention", `Needs attention${attention?.length ? ` (${attention.length})` : ""}`]];
  return (
    <div className="space-y-6" data-tour="pt-candidates-page" data-testid="candidates-page">
      <PageHeader title="Candidates" subtitle="Everyone who applied or was uploaded, across all jobs. Contact details are masked in lists."
        actions={<IntakeActions />} />
      <div className="flex gap-1 border-b border-line">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`candidates-tab-${k}`}
            className={cn("-mb-px border-b-2 px-3 py-2.5 text-sm transition-colors duration-150", tab === k ? "border-brand font-medium text-brand" : "border-transparent text-ink-secondary hover:text-ink")}>
            {label}
          </button>
        ))}
      </div>
      <div key={tab} className="tl-page">{tab === "all" ? <CandidatesTable showJobs testId="global-candidates-table" /> : <NeedsAttention />}</div>
    </div>
  );
}
